import React, {useCallback, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {SmoothingType} from '../../../../generated/graphql';
import {useProjectParams} from '../../../../pages/ProjectPage/useProjectParams';
import {type SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {
  calculateIsSettingEnabled,
  type SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {useRampFlagUseBackendSmoothing} from '../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {smoothingOptionsMeta} from '../../../elements/SmoothingConfig';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {SettingSlider} from '../components/SettingSlider';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {type LinePlotSettings, type SettingLevel} from '../types';
import {DEFAULT_SMOOTHING_SETTINGS} from './linePlotDefaults';
import {useProfiledSmoothing} from './useProfiledSmoothing';

export const EMPTY_SMOOTHING_SETTINGS = {
  smoothingType: undefined,
  smoothingWeight: undefined,
};

type SmoothingSelectOption = {
  value: SmoothingType;
  label: string;
};

type SmoothingMetadataType = {
  defaultValue: number;
  min: number;
  max: number;
  step: number;
};

export const getSmoothingTypeDisplayName = (
  smoothingType: SmoothingType,
  isBackendSmoothingEnabled: boolean
): string => {
  const meta = smoothingOptionsMeta[smoothingType];
  if (smoothingType === SmoothingType.Exponential) {
    return `${meta.displayName}${
      isBackendSmoothingEnabled ? '' : ' (deprecated)'
    }`;
  }
  return meta.displayName;
};

const getSmoothingOptions = (
  isBackendSmoothingEnabled: boolean
): SmoothingSelectOption[] => {
  return Object.keys(smoothingOptionsMeta).map(val => ({
    label: getSmoothingTypeDisplayName(
      val as SmoothingType,
      isBackendSmoothingEnabled
    ),
    value: val as SmoothingType,
  }));
};

const getSmoothingMetadata = (
  smoothingOption: SmoothingSelectOption
): SmoothingMetadataType => {
  const data = smoothingOptionsMeta[smoothingOption.value].defaults;
  return {
    defaultValue: data.default,
    min: data.min,
    max: data.max,
    step: data.step,
  };
};

export type LinePlotSmoothingProps = {
  className?: string;
  clearSetting: ClearSettingFnType;
  /**
   * These are the values that are displayed to the user. We want to show the
   * inherited values when relevant.
   */
  displayValues: {
    smoothingType?: SmoothingType;
    smoothingWeight?: number;
  };
  numOverrides?: SettingOverrideCounts;
  settingLevel: SettingLevel;
  /**
   * These are the values that match in the view spec depending on the setting level.
   */
  values: {
    smoothingType?: SmoothingType;
    smoothingWeight?: number;
  };
  trackSetting: (setting: SettingType, value: string | undefined) => void;
  updateSetting: (
    settings: Pick<LinePlotSettings, 'smoothingType' | 'smoothingWeight'>
  ) => void;
};

export const LinePlotSmoothing = ({
  className,
  clearSetting,
  displayValues,
  numOverrides,
  settingLevel,
  values,
  trackSetting,
  updateSetting,
}: LinePlotSmoothingProps) => {
  const {entityName} = useProjectParams();
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing(entityName);

  useProfiledSmoothing({
    settingLevel,
    smoothingWeight: displayValues.smoothingWeight,
    smoothingType: displayValues.smoothingType,
  });

  const smoothingOptions = useMemo(
    () => getSmoothingOptions(isBackendSmoothingEnabled),
    [isBackendSmoothingEnabled]
  );

  const smoothingTypeOption = useMemo(() => {
    if (displayValues.smoothingType == null) {
      return {
        value: DEFAULT_SMOOTHING_SETTINGS.smoothingType,
        label: getSmoothingTypeDisplayName(
          DEFAULT_SMOOTHING_SETTINGS.smoothingType,
          isBackendSmoothingEnabled
        ),
      };
    }
    return {
      value: displayValues.smoothingType,
      label: getSmoothingTypeDisplayName(
        displayValues.smoothingType,
        isBackendSmoothingEnabled
      ),
    };
  }, [displayValues.smoothingType, isBackendSmoothingEnabled]);

  const onSmoothingTypeChange = useCallback(
    (selected: SmoothingSelectOption | null) => {
      if (selected != null) {
        updateSetting({
          smoothingType: selected.value,
          smoothingWeight: getSmoothingMetadata(selected).defaultValue, // reset
        });

        trackSetting('smoothingType', selected.value.toString());
      }
    },
    [trackSetting, updateSetting]
  );

  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings: EMPTY_SMOOTHING_SETTINGS,
    numOverrides,
    settingKey: 'smoothing',
    settingLevel,
  });

  const isSmoothingTypeNone = smoothingTypeOption.value === SmoothingType.None;

  return (
    <div className={twMerge('flex flex-col', className)}>
      <div className="flex items-center justify-between">
        <SettingLabel text="Smoothing" className="shrink-0" />
        <ResetSettingButton
          isEnabled={calculateIsSettingEnabled('smoothing', values)}
          onButtonClick={() =>
            clearSetting(settingLevel, 'smoothing', EMPTY_SMOOTHING_SETTINGS)
          }
          settingLevel={settingLevel}
        />
      </div>
      <div className="mt-4 flex w-full gap-8">
        <SettingSelect
          className="w-full"
          options={smoothingOptions}
          value={smoothingTypeOption}
          onChange={onSmoothingTypeChange}
        />

        {!isSmoothingTypeNone && (
          <SmoothingSettingSlider
            smoothingWeight={displayValues.smoothingWeight}
            smoothingTypeOption={smoothingTypeOption}
            updateSetting={updateSetting}
            trackSetting={trackSetting}
          />
        )}
        <div
          className={twMerge(
            isSmoothingTypeNone && clearSettingRows.length > 0 ? 'mt-12' : ''
          )}>
          <ClearSettingsRows rows={clearSettingRows} />
        </div>
      </div>
    </div>
  );
};

export const SmoothingSettingSlider = ({
  smoothingWeight,
  smoothingTypeOption,
  updateSetting,
  trackSetting,
}: {
  smoothingWeight: number | undefined;
  smoothingTypeOption: SmoothingSelectOption;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
  trackSetting: (setting: SettingType, value: string | undefined) => void;
}) => {
  const {min, max, step, defaultValue} = useMemo(
    () => getSmoothingMetadata(smoothingTypeOption),
    [smoothingTypeOption]
  );

  const updateSettingValue = useCallback(
    (newVal: number) => {
      updateSetting({
        smoothingWeight: newVal,
      });
      trackSetting('smoothingWeight', newVal.toString());
    },
    [trackSetting, updateSetting]
  );

  return (
    <SettingSlider
      dataTest="line-plot-smoothing-slider"
      name="smoothing"
      min={min}
      max={max}
      step={step}
      sliderValue={smoothingWeight == null ? defaultValue : smoothingWeight}
      updateSetting={updateSettingValue}
    />
  );
};
